import React, { FC, ReactNode, useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import axiosInstant from "../../../../../api";
import { productAiPresenceEndpoint, productAiPresenceItemEndpoint } from "../../../../../api/endpoints";
import { useUser } from "../../../../../hooks/useUser";
import { isInternal } from "../../../../../utils";
import { IconAlertGlyph, IconClockGlyph } from "../../../videos/selectedVideoCardV2/panels";
import { PollIndicator, ScoreState } from "../../../videos/selectedVideoCardV2/performance/ScoreState";
import type { ISingleProduct } from "../../../productsV2/productListView/constants";
import { SECTION_IDS } from "../components/SectionRail";
import { SectionCard, StateBlockSkeleton } from "../components/SectionCard";
import { cvBrandIdForViewer } from "../consumerVoice/cvModel";
import { AIP_PLATFORM_LABELS, AiPresenceAudit, isAipRunning } from "../../../../../types/aiPresence";
import { aipScoreTone } from "../../../../../components/AiPresence/AiPresenceReportView";
import { AiPresenceReportDialog } from "../../../../../components/AiPresence/AiPresenceReportDialog";
import { useStrategyFromAnalysis } from "../../../../../components/ProposalFlow/useStrategyFromAnalysis";

/**
 * AI Presence section — read-only, next to Consumer Voice: the newest ready
 * run's AI Presence / Discovery / Recommendation and per-platform scores,
 * with a "View report" link. Runs, hints and history live in the settings
 * modal's AI Presence tab (same split as the Consumer Voice section). Same
 * brand rule as Consumer Voice (cvBrandIdForViewer); polls every 8 seconds
 * while a first run is in flight.
 */

const TITLE = "AI Presence";
const SECTION_ID = SECTION_IDS.aiPresence;
const POLL_MS = 8000;

interface Props {
  product: ISingleProduct;
  scopeBrandId: number | null;
  reloadKey: number;
  pollPaused?: boolean;
  className?: string;
}

function Tile({ label, value, sub }: { label: string; value: number | null | undefined; sub?: string }) {
  return (
    <div className="flex-1 min-w-[130px] border border-border-token rounded-xl p-3 bg-surface">
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-muted mb-1.5">{label}</div>
      <span className={`text-[22px] font-bold leading-none rounded-lg px-2 py-1 ${aipScoreTone(value)}`}>{value == null ? "—" : value}</span>
      {sub ? <div className="text-[11.5px] text-text-2 mt-1.5">{sub}</div> : null}
    </div>
  );
}

const AiPresenceSectionView: FC<Props> = ({ product, scopeBrandId, reloadKey, pollPaused, className }) => {
  const desireid = product?.desireid ?? null;
  const { role } = useUser();
  const brandId = cvBrandIdForViewer(product?.brands, scopeBrandId, isInternal(role));
  const [rows, setRows] = useState<AiPresenceAudit[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);
  const createStrategy = useStrategyFromAnalysis();

  const load = useCallback(async () => {
    if (!desireid || !brandId) return;
    try {
      const res = await axiosInstant.get(`${productAiPresenceEndpoint(desireid)}?brandId=${brandId}`);
      setRows(Array.isArray(res.data) ? res.data : []);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [desireid, brandId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const ready = (rows || []).find((r) => r.status === "ready") || null;
  const running = (rows || []).some(isAipRunning);
  useEffect(() => {
    if (!running || pollPaused || ready) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [running, pollPaused, ready, load]);

  const card = (children: ReactNode, subtitle?: ReactNode) => (
    <SectionCard id={SECTION_ID} title={TITLE} subtitle={subtitle} className={className}>
      {children}
    </SectionCard>
  );

  if (!brandId) {
    return card(<ScoreState skin="empty" title="Not available" body="No manufacturer brand on this product, so there's nothing to analyze." />);
  }
  if (failed && !rows) {
    return card(<ScoreState skin="error" icon={<IconAlertGlyph size={20} strokeWidth={1.7} />} title="Couldn't load AI Presence" body="Something went wrong on our end." />);
  }
  if (!rows) return card(<StateBlockSkeleton />);

  if (ready) {
    const ps = ready.platform_scores || {};
    return card(
      <>
        <div className="flex flex-wrap gap-3">
          <Tile label="AI Presence" value={ready.overall_score} />
          <Tile label="Discovery" value={ready.discovery_score} />
          <Tile label="Recommendation" value={ready.recommendation_score} />
          {Object.keys(ps).map((k) => (
            <Tile key={k} label={AIP_PLATFORM_LABELS[k] || k} value={ps[k].score} />
          ))}
        </div>
        <button type="button" onClick={() => setViewing(ready.id)} className="mt-3 text-[12.5px] font-semibold text-accent-text hover:underline">
          View report
        </button>
        {viewing !== null && desireid ? (
          <AiPresenceReportDialog
            endpoint={productAiPresenceItemEndpoint(desireid, viewing, brandId)}
            onClose={() => setViewing(null)}
            onCreateStrategy={(audit) => createStrategy("aip", { id: audit.id, desireid }, product?.title)}
          />
        ) : null}
      </>,
      `Analyzed ${ready.scored_at ? dayjs(ready.scored_at).format("MMM D, YYYY") : ""}${running ? " · a new run is in progress" : ""}`,
    );
  }
  if (running) {
    return card(
      <ScoreState
        skin="working"
        icon={<IconClockGlyph size={20} strokeWidth={1.7} />}
        title="Analysis running"
        body="Claude, Gemini and OpenAI are being asked the shopping prompts. The scores will appear here on their own."
        footer={
          <div className="flex justify-center">
            <PollIndicator>Checking every 8 seconds</PollIndicator>
          </div>
        }
      />,
    );
  }
  if (rows.length) {
    return card(
      <ScoreState
        skin="error"
        icon={<IconAlertGlyph size={20} strokeWidth={1.7} />}
        title="The last analysis didn't finish"
        body="Run it again from this product's Settings → AI Presence."
      />,
    );
  }
  return card(
    <ScoreState
      skin="empty"
      title="No analysis yet"
      body="Run one from this product's Settings → AI Presence to see how often AI recommends it."
    />,
  );
};

export const AiPresenceSection = React.memo(AiPresenceSectionView);
